import slugify from 'slugify';

export default class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this.optionLists = Array.from(this.querySelectorAll('.option-list'));
    this.staticOptionLists = this.optionLists.filter((optionList) => !optionList.classList.contains('dynamic-option'));
    this.priceText = document.getElementById('price-text');
    this.productInfo = document.querySelector('.product-info');
    this.currentVariantId = document.getElementById('current_variant_id');

    // affirm text
    this.affirmText = document.querySelector('.affirm-as-low-as');
  }

  connectedCallback() {
    // affirm
    affirm.ui.ready(function () {
      affirm.ui.refresh();
    });

    // update static options
    if (this.staticOptionLists.length) {
      this.updateStaticOptions(true);
    }

    // handle logic for variant picker option lists
    this.optionLists.forEach((optionList) => {
      const radioButtons = Array.from(optionList.querySelectorAll('input[type="radio"]'));
      const selectedItemEl = optionList.querySelector('.selected-item');
      const optionImage = optionList.querySelector('.clicker img');

      // add different listeners for dynamic/static options
      if (optionList.classList.contains('dynamic-option')) {
        radioButtons.forEach((radioButton) => {
          radioButton.addEventListener('change', () => {
            // remove no-selection-made class
            optionList.classList.remove('no-selection-made');

            this.productInfo.classList.add('loading');

            // update selected item text
            selectedItemEl.innerHTML = radioButton.value;

            // get the new variant
            const newVariantId = radioButton.dataset.variantId;

            // update url
            const newUrl = this.generateUrl({
              data: {
                key: 'variant',
                value: newVariantId,
              },
            });

            history.pushState(null, '', newUrl);

            // build url for section fetch
            const sectionUrl = `${window.location.pathname}?section_id=main-product&variant=${newVariantId}`;

            // fetch the section
            this.renderPage({
              url: sectionUrl,
              updateOptions: false,
            });
          });

          radioButtons.forEach((radioButton) => {
            radioButton.addEventListener('click', () => {
              // remove no-selection-made class
              optionList.classList.remove('no-selection-made');
            });
          });
        });
      } else {
        radioButtons.forEach((radioButton) => {
          radioButton.addEventListener('change', () => {
            // remove no-selection-made class
            optionList.classList.remove('no-selection-made');

            // get radio button name
            let name = radioButton.name;

            // remove 'static-option:' from name
            name = name.replace('static-option:', '');

            // then slugify the name
            name = slugify(name, { lower: true });

            // update selected item text
            selectedItemEl.innerHTML = radioButton.value;

            // turn radio button value to a slug
            const value = slugify(radioButton.value, { lower: true });

            // update url
            const newUrl = this.generateUrl({
              data: {
                key: name,
                value,
              },
            });

            history.pushState(null, '', newUrl);

            // check if there is an image to update, e.g. gold color icons
            const newImage = radioButton.dataset.img;

            if (optionImage && newImage) {
              optionImage.src = newImage;
            }
          });

          radioButton.addEventListener('click', () => {
            // remove no-selection-made class
            optionList.classList.remove('no-selection-made');
          });
        });
      }
    });

    window.addEventListener('popstate', this.onHistoryChange.bind(this));
  }

  generateUrl({ data }) {
    const { key, value } = data;

    // start with empty params
    let params = {
      variant: '',
      'gold-color': '',
      'ring-size': '',
    };

    // get current params
    const currentParams = new URLSearchParams(window.location.search);

    // loop through current params and set new params
    for (const [key, value] of currentParams.entries()) {
      params[key] = value;
    }

    // set new value
    params[key] = value;

    // normalize params
    if (params.variant === '') {
      delete params.variant;
    }

    if (params['gold-color'] === '') {
      delete params['gold-color'];
    }

    if (params['ring-size'] === '') {
      delete params['ring-size'];
    }

    // build new search string
    const queryString = this.toQueryString(params);

    return queryString;
  }

  renderPage({ url, updateOptions = false }) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const resultsMarkup = new DOMParser().parseFromString(html, 'text/html');

        // get new price text and replace current price text
        const newPriceText = resultsMarkup.querySelector('#price-text');
        this.priceText.innerHTML = newPriceText.innerHTML;

        // update affirm text
        this.affirmText.setAttribute('data-amount', parseInt(newPriceText.dataset.price));
        affirm.ui.refresh();

        // get new variant id and replace
        const newVariantId = resultsMarkup.querySelector('#current_variant_id');
        this.currentVariantId.value = newVariantId.value;

        // get new option lists
        const newOptions = Array.from(
          resultsMarkup.querySelectorAll('.option-list.dynamic-option input[type="radio"]'),
        );

        newOptions.forEach((newOption) => {
          const id = newOption.id;
          const oldRadioButton = document.getElementById(id);

          // change checked value
          if (updateOptions) {
            oldRadioButton.checked = newOption.checked;
          }

          // change variant id values
          if (newOption.dataset.variantId !== oldRadioButton.dataset.variantId) {
            oldRadioButton.dataset.variantId = newOption.dataset.variantId;
          }
        });

        if (updateOptions) {
          const newSelectedDynamicItems = Array.from(
            resultsMarkup.querySelectorAll('.option-list.dynamic-option .selected-item'),
          );

          newSelectedDynamicItems.forEach((newSelectedItem) => {
            const id = newSelectedItem.id;
            const oldSelectedItem = document.getElementById(id);

            oldSelectedItem.innerHTML = newSelectedItem.innerHTML;
          });
        }

        this.productInfo.classList.remove('loading');
      });
  }

  updateStaticOptions(addInitializedClass = false) {
    const searchParams = new URLSearchParams(window.location.search);

    // check if there is an element in static option lists that has data-name of 'Gold Color'
    const goldColorOption = this.staticOptionLists.find((option) => option.dataset.name === 'Gold Color');
    const ringSizeOption = this.staticOptionLists.find((option) => option.dataset.name === 'Ring Size');

    if (goldColorOption) {
      this.staticOptionUpdate({ title: 'Gold Color', searchParams, addInitializedClass });
    }

    if (ringSizeOption) {
      this.staticOptionUpdate({ title: 'Ring Size', searchParams, addInitializedClass });
    }
  }

  staticOptionUpdate({ title, searchParams, addInitializedClass }) {
    const slug = slugify(title, { lower: true });

    const paramVal = searchParams.get(slug);

    // get radio buttons
    const radios = Array.from(document.querySelectorAll(`input[name="static-option:${title}"]`));

    if (paramVal) {
      radios.forEach((radio) => {
        if (radio.id === `${slug}-${paramVal}`) {
          radio.checked = true;
        }
      });
    } else {
      // radios[0].checked = true;
    }

    // get selected item
    const optionList = document.querySelector(`.option-list:not(.dynamic-option)[data-name="${title}"]`);
    const selectedItem = optionList.querySelector(`.selected-item`);

    if (selectedItem && paramVal) {
      selectedItem.innerHTML = radios.find((radio) => radio.checked).value;
    }

    if (addInitializedClass) {
      optionList.classList.add('initialized');
      optionList.classList.remove('no-selection-made');
    }
  }

  onHistoryChange() {
    const variantId = new URLSearchParams(window.location.search).get('variant');
    const sectionUrl = `${window.location.pathname}?section_id=main-product${variantId ? `&variant=${variantId}` : ''}`;

    this.renderPage({
      url: sectionUrl,
      updateOptions: true,
    });

    if (this.staticOptionLists.length) {
      this.updateStaticOptions();
    }
  }

  toQueryString(obj) {
    let queryString = Object.keys(obj)
      .map((key) => {
        const value = obj[key];
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      })
      .join('&')
      .replace(/%20/g, '+'); // Wordpress style '+' separator
    return queryString ? '?' + queryString : '';
  }
}
